import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    EditButton,
    DeleteButton,
    TopToolbar,
    CreateButton,
    useNotify,
    useRefresh,
    Button,
} from 'react-admin';

import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, CircularProgress } from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';

// ---------------------------------------------------------------------------
// Import button
// ---------------------------------------------------------------------------

const ImportButton = () => {
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const fileRef = React.useRef(null);
    const notify = useNotify();
    const refresh = useRefresh();

    const handleOpen = () => {
        setResult(null);
        setOpen(true);
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(true);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const auth = localStorage.getItem('auth');
            const { bearerToken: token } = auth ? JSON.parse(auth) : {};
            const apiUrl = process.env.REACT_APP_API_URL ?? '';

            const response = await fetch(`${apiUrl}/admin/v1/hmrc-job-titles/import`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message ?? 'Import failed');
            }

            const data = await response.json();
            setResult(data);
            refresh();
        } catch (err) {
            notify(err.message ?? 'Import failed', 'error');
        } finally {
            setLoading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <>
            <Button label="Import Excel" onClick={handleOpen}>
                <PublishIcon />
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Import HMRC job titles</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Upload an Excel (.xlsx / .xls) or CSV file with job titles in the first
                        column. A header row labelled "Job Title" is automatically skipped.
                        Existing titles are skipped; previously deleted titles are restored.
                    </Typography>
                    <Box mt={2}>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFile}
                            disabled={loading}
                        />
                    </Box>
                    {loading && (
                        <Box mt={2} display="flex" alignItems="center" style={{ gap: 8 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Importing…</Typography>
                        </Box>
                    )}
                    {result && (
                        <Box mt={2}>
                            <Typography variant="body2" style={{ color: 'green' }}>
                                ✓ {result.created} created, {result.restored} restored, {result.skipped} skipped
                            </Typography>
                            {result.errors?.length > 0 && (
                                <Box mt={1}>
                                    {result.errors.map((err, i) => (
                                        <Typography key={i} variant="body2" color="error">{err}</Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button label="Close" onClick={() => setOpen(false)} />
                </DialogActions>
            </Dialog>
        </>
    );
};

// ---------------------------------------------------------------------------
// List actions
// ---------------------------------------------------------------------------

const HmrcJobTitleListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ImportButton />
    </TopToolbar>
);

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const HmrcJobTitleList = props => (
    <List
        {...props}
        actions={<HmrcJobTitleListActions />}
        filters={[<TextInput key="q" label="Search" source="q" alwaysOn />]}
        sort={{ field: 'title', order: 'ASC' }}
        perPage={50}
        empty={false}
    >
        <Datagrid>
            <TextField source="title" label="Job Title" />
            <DateField source="created_at" label="Added" />
            <DateField source="updated_at" label="Updated" />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const HmrcJobTitleCreate = props => (
    <Create {...props} title="Add HMRC Job Title">
        <SimpleForm>
            <TextInput source="title" label="Job Title" fullWidth />
        </SimpleForm>
    </Create>
);

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

export const HmrcJobTitleEdit = props => (
    <Edit {...props} title="Edit HMRC Job Title" undoable={false}>
        <SimpleForm>
            <TextInput source="title" label="Job Title" fullWidth />
        </SimpleForm>
    </Edit>
);
